import { Tooltip } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import type { DispoCalendarEvent } from '@shared/types'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'

/** Returns dark/light text colour that reads well against the given hex background. */
function contrastTextColor(hex: string): string {
  const h = hex.replace('#', '')
  if (h.length !== 6) return '#374151'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  // perceived luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.55 ? '#374151' : '#F9FAFB'
}

/** Blends 30 % of the custom colour with 70 % white → clearly visible pastel background. */
function pastelize(hex: string): string {
  const h = hex.replace('#', '')
  if (h.length !== 6) return '#F3F4F6'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const pr = Math.round(r * 0.3 + 255 * 0.7)
  const pg = Math.round(g * 0.3 + 255 * 0.7)
  const pb = Math.round(b * 0.3 + 255 * 0.7)
  return `#${pr.toString(16).padStart(2, '0')}${pg.toString(16).padStart(2, '0')}${pb.toString(16).padStart(2, '0')}`
}

const PLANNING_TYPE_STYLES: Record<string, string> = {
  SUPPORT: 'bg-blue-100 border-blue-400 text-blue-800',
  APPOINTMENT_FIX: 'bg-brand-100 border-brand-400 text-brand-900',
  APPOINTMENT_PROPOSAL: 'bg-purple-100 border-purple-400 text-purple-800',
  APPOINTMENT_PRIVATE: 'bg-gray-100 border-gray-400 text-gray-700',
  VACATION: 'bg-green-100 border-green-500 text-green-900',
  ILLNESS: 'bg-red-100 border-red-500 text-red-900',
  ABSENCE: 'bg-orange-100 border-orange-400 text-orange-800',
  STAND_BY: 'bg-yellow-100 border-yellow-400 text-yellow-800',
  OVERTIME: 'bg-teal-100 border-teal-400 text-teal-800',
  CUSTOM: 'bg-indigo-100 border-indigo-400 text-indigo-800',
  AUSSENEINSATZ: 'bg-amber-100 border-amber-500 text-amber-900',
}

function getEventStyle(type: string): string {
  return PLANNING_TYPE_STYLES[type] ?? 'bg-gray-100 border-gray-300 text-gray-700'
}

interface DispositionCalendarEventBarProps {
  event: DispoCalendarEvent
  /** Left offset as percentage of the day column (0–100) */
  left: number
  /** Width as percentage of the day column (0–100) */
  width: number
  /** Stack row index for overlap handling */
  stackRow: number
  /** Total stacked rows for this time slot */
  totalRows: number
  /** Whether the event starts before the visible window (show left arrow) */
  clippedLeft?: boolean
  /** Whether the event ends after the visible window (show right arrow) */
  clippedRight?: boolean
  onClick?: (event: DispoCalendarEvent) => void
}

const DAY_HEIGHT_PX = 48

export function DispositionCalendarEventBar({
  event,
  left,
  width,
  stackRow,
  totalRows,
  clippedLeft = false,
  clippedRight = false,
  onClick,
}: DispositionCalendarEventBarProps) {
  const rowHeight = DAY_HEIGHT_PX / Math.max(totalRows, 1)
  const topPx = stackRow * rowHeight
  const heightPx = rowHeight - 2

  const startLabel = format(new Date(event.startTs * 1000), 'HH:mm', { locale: de })
  const endLabel = event.endTs != null ? format(new Date(event.endTs * 1000), 'HH:mm', { locale: de }) : ''
  const timeLabel = endLabel ? `${startLabel}–${endLabel}` : startLabel
  const customLabel = event.planningAdditionalTitle || event.supportTypeName || undefined
  const label = event.title || event.ticketTitle || event.companyName || customLabel || event.type

  // When TANSS provides custom colors, apply them as inline styles instead of Tailwind classes.
  const hasCustomColor = !!event.customColor
  const cssClass = hasCustomColor ? '' : getEventStyle(event.type)
  const inlineColorStyle = hasCustomColor
    ? {
        backgroundColor: pastelize(event.customColor!),
        borderLeftColor: event.customColor,
        color: contrastTextColor(event.customColor!),
      }
    : undefined

  const tooltipContent = (
    <div>
      <div className="font-semibold">{label}</div>
      <div className="opacity-80">{timeLabel}</div>
      {customLabel && customLabel !== label && (
        <div className="opacity-70 text-[11px] mt-0.5">{customLabel}</div>
      )}
    </div>
  )

  return (
    <Tooltip content={tooltipContent} side="top" delayDuration={200}>
      <div
        className={cn(
          'absolute rounded border-l-2 overflow-hidden px-1 text-[10px] leading-tight font-medium select-none',
          onClick ? 'cursor-pointer hover:brightness-90' : '',
          cssClass,
        )}
        style={{
          left: `${left}%`,
          width: `${Math.max(width, 2)}%`,
          top: `${topPx}px`,
          height: `${heightPx}px`,
          minWidth: '2px',
          ...inlineColorStyle,
        }}
        onClick={onClick ? () => onClick(event) : undefined}
      >
        {clippedLeft && (
          <ChevronLeft className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 opacity-70 shrink-0" />
        )}
        <span className={cn('truncate block', clippedLeft && 'pl-3', clippedRight && 'pr-3')}>
          {label}
        </span>
        {clippedRight && (
          <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 opacity-70 shrink-0" />
        )}
      </div>
    </Tooltip>
  )
}
